import { AfterContentChecked, Component, DoCheck, forwardRef, Input, OnInit } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomElement } from '../../../classes/custom-element.class';
import { BsLocaleService, defineLocale, nlLocale } from 'ngx-bootstrap';
import moment from 'moment-es6';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimeInputComponent),
    multi: true
};

@Component({
    selector: 'chassis-datetime-input',
    templateUrl: './datetime.component.html',
    styleUrls: ['./datetime.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class DateTimeInputComponent extends CustomElement implements OnInit, AfterContentChecked, DoCheck {

    /**
     * Type month or year picker do not work yet without selecting a
     * day. A pull request is available but not yet approved:
     * https://github.com/valor-software/ngx-bootstrap/pull/3354
     *
     * Type datetime picker is not yet available, a feature request
     * exists and is added to the ngx-bootstrap roadmap.
     * https://github.com/valor-software/ngx-bootstrap/issues/3340
     */
    @Input() type = 'date';

    @Input() placeholder = '';

    @Input() min: string;

    @Input() max: string;

    @Input() disabled: boolean;

    @Input() required: boolean;

    @Input() icon = true;

    /**
     * The value that holds the non formatted date for
     * datetime pickers.
     */
    public innerDateValue: string;

    /**
     * The value that holds the noon formatted time for
     * datetime pickers.
     */
    public innerTimeValue: string;

    /**
     * The value that holds a non formatted value.
     */
    public inner: string | Array<string>;

    /**
     * The date format to use. To set your own date format you
     * can use variety of formats provided by moment.js
     * https://momentjs.com/docs/#/displaying/format/
     */
    @Input() format: string;

    constructor(protected localeService: BsLocaleService) {
        super();
    }

    public ngOnInit() {
        this.setLocale();
        this.mutateAttributeValues();
    }

    protected setLocale() {
        // Preload the nl locale for the date and time picker
        defineLocale('nl', nlLocale);

        const locale = 'nl';

        // Set the locale for the date and time picker
        this.localeService.use(locale);

        // Set the locale for moment.js
        moment.locale(locale);
    }

    protected mutateAttributeValues() {
        // We'll set the attributes value to boolean values
        // even when no value was given. This is because these
        // attributes are boolean attributes and do not need
        // a value
        this.disabled = this.disabled !== undefined && this.disabled !== false;
        this.required = this.required !== undefined && this.required !== false;

        this.placeholder = this.placeholder || '';

        // Set a format if no format was provided
        if (!this.format) {
            this.mutateFormatAttributeValue();
        }
    }

    /**
     * Set the date format to use. The date formats need to
     * be a format provided by moment.js
     * https://momentjs.com/docs/#/displaying/format/
     */
    protected mutateFormatAttributeValue() {
        // Get the locale data from moment.js
        const data = (<any>moment().localeData())._longDateFormat;

        // Get the year-month-day format for the selected locale
        const ymd = data.L;

        // Remove the days of the year-month-day format
        let ym = ymd.replace(/\DDD/g, '');
        ym     = ym.replace(/\DD/g, '');
        ym     = (ym.substring(0, 1) === '-' || ym.substring(0, 1) === '/')
            ? ym.substring(1) : ym;

        switch (this.type) {
            case 'year':
                this.format = 'YYYY';
                break;
            case 'year-range':
                this.format = 'YYYY';
                break;
            case 'month':
                this.format = ym;
                break;
            case 'month-range':
                this.format = ym;
                break;
            case 'week-range':
                this.format = 'W';
                break;
            case 'week':
                this.format = 'W';
                break;
            case 'time':
                this.format = 'HH:mm';
                break;
            default:
                this.format = ymd;
                break;
        }
    }

    ngAfterContentChecked() {
        // Check if the value has been emptied, if so we'll
        // have to empty the inner value too.
        if (this.val !== undefined && !this.val) {
            this.inner = null;
        }
    }

    ngDoCheck() {
        // If val is different (because of external model change), set inner to the same value
        if (this.val && this.inner && moment(this.val).format('YYYY-MM-DD HH:mm:ss') !== moment(this.inner, this.format).format('YYYY-MM-DD HH:mm:ss')) {
            this.inner = moment(this.val).format(this.format);
        }
    }

    public get config() {
        return {
            dateInputFormat: this.format,
            rangeInputFormat: this.format,
            minDate: this.min,
            maxDate: this.max,
            isDisabled: this.disabled,
        };
    }

    public mergeDateWithTime(value: string) {
        const date = moment(value);

        // The current time value set
        const time = (this.timeValue ? moment(this.timeValue)
            : moment().hours(0).minutes(0).seconds(0));

        // Add the time to the selected date
        date
            .hours(time.hours())
            .minutes(time.minutes())
            .seconds(0)
            .milliseconds(0);

        this.inputValue = date.toISOString() || '';
    }

    public mergeTimeWithDate(value: string) {
        // Add the given time to a new date instance
        // so it is easy to request the time values
        const time = moment(value, 'HH:mm');

        // The current date value set
        const date = (this.dateValue ? moment(this.dateValue, this.format)
            : null);

        // If the date input was invalid or empty, we
        // don't want to return a value
        if (!date) {
            return '';
        }

        // Add the selected time to the date
        date
            .hours(time.hours())
            .minutes(time.minutes())
            .seconds(0)
            .milliseconds(0);

        this.inputValue = date.toISOString() || '';
    }

    /**
     * The value property setter.
     *
     * @param {string | Array<string>} value
     */
    public set inputValue(value: string | Array<string>) {
        this.inner = value;

        // Pass the formatted date, time or datetime value
        // to the input component
        this.val = this.formatDateTimeValue(value);
    }

    /**
     * Format the given date, time or datetime to the appropriate
     * format.
     *
     * @param {string} value
     * @return {string}
     */
    protected formatDateTimeValue(value: string | Array<string>): string | Array<string> {
        if (value instanceof Array) {
            // If the value contains an array, we'll map and return
            // the array's values to formatted values
            return this.formatArrayDateTimeValues(value);
        }

        // Time values do not have to be formatted
        // if (this.isType('time')) {
        //     return value;
        // }

        // Contains a ISO format date
        const date = moment(value);

        let format;

        // Get the right format for the type
        switch (this.type) {
            case 'year':
                format = 'YYYY';
                break;
            case 'year-range':
                format = 'YYYY';
                break;
            case 'month':
                format = 'YYYY-MM';
                break;
            case 'month-range':
                format = 'YYYY-MM';
                break;
            case 'week':
                format = 'YYYY-W';
                break;
            case 'week-range':
                format = 'YYYY-W';
                break;
            case 'datetime':
                format = 'YYYY-MM-DD HH:mm:ss';
                break;
            case 'time':
                format = 'HH:mm';
                break;
            default:
                format = 'YYYY-MM-DD';
                break;
        }

        return date.isValid() ? date.format(format)
            : '';
    }

    protected formatArrayDateTimeValues(values: Array<string>): Array<string> {
        return values.map((value: string) => {
            return <string>this.formatDateTimeValue(value);
        });
    }

    /**
     * The value property getter.
     *
     * @return {string | Array<string>}
     */
    public get inputValue(): string | Array<string> {
        if (!this.inner && this.val) {
            this.setInitialValue();
        }

        return this.inner;
    }

    protected setInitialValue() {
        let value = this.val;

        if (this.isType(['date-range', 'year-range', 'month-range', 'week-range'])) {
            if (value instanceof Array) {
                value = this.formatInitialRangeValue(value);
            }
        } else if (this.isType(['datetime'])) {
            value = moment(this.val);

            this.dateValue = value.format(this.format);
            this.timeValue = value.format('YYYY-MM-DD HH:mm');
        } else if (this.isType(['time'])) {
            value = moment(this.val, 'HH:mm:ss');

            this.timeValue = value.format('YYYY-MM-DD HH:mm:ss');
        } else {
            value = moment(this.val).format(this.format);
        }

        this.inner = value;
    }

    protected formatInitialRangeValue(range: Array<string>) {
        for (const key in range) {
            if (range.hasOwnProperty(key)) {
                range[key] = moment(range[key]).format(this.format);
            }
        }

        return range;
    }

    /**
     * The date value has a getter so the initial value will be set
     * on request of the date value. Just like the input value, but
     * the type datetime will not trigger the input value getter.
     *
     * @return {string}
     */
    public get dateValue() {
        if (!this.inner && this.val) {
            this.setInitialValue();
        }

        return this.innerDateValue;
    }

    public set dateValue(value: string) {
        this.innerDateValue = value;
    }

    /**
     * For consistency with the date value.
     *
     * @return {string}
     */
    public get timeValue() {
        if (!this.inner && this.val) {
            this.setInitialValue();
        }

        return this.innerTimeValue;
    }

    public set timeValue(value: string) {
        this.innerTimeValue = value;
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/components/datetime/datetime/datetime.component.ts